import React from 'react';
import styled from 'styled-components';
import { Body7, Container } from '@ui';
import { NumberField, Select } from '@ui/inputs';
import { TransparentFlatButton } from '@ui/buttons';
import { SpecEditorContext } from './SpecEditorContext';
import { REASONING_EFFORTS, CONTEXT_SETTING_KEYS, CONTEXT_SETTING_VALUES } from './specSchema';
import { ModelSelect } from './ModelSelect';
import { PromptEditor } from './PromptEditor';
import { PreviewDock } from './PreviewDock';
import { Consequence, MicroLabel, ReadOnlyJson, Row } from './styles';

// Question 3 — how it is asked. The score_gen body.
//
// `prompt` and `format_prompt` sit SIDE BY SIDE, with the declared output shape beside them, so
// the prose that asks for a shape and the schema that guarantees it can be cross-read in one
// glance. That pairing is the point; previously both were string cells in a JSON tree.
//
// Two fields are deliberately NOT turned into pickers:
//   * `model` is a dropdown over the live model catalog (`GET v2/genai/models`), the same list
//     every other model picker in the app shows. It degrades to free text when that catalog is
//     empty and keeps an "Other…" escape, because the scoring engine validates against its own
//     price table and the two lists are not guaranteed to be identical.
//   * The token budgets carry HOUSE ADVICE, labelled as advice. The loader enforces no floor.

const OMITTED = '__omitted__';
const CONTEXT_SETTINGS_ID = 'score-gen-context-settings';

const Columns = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: 1.4fr 1fr 1fr;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const Studio = ({ specId, isCreate, isDirty }) => {
  const { definition, setValue, removeValue } = React.useContext(SpecEditorContext);

  const gen = (definition || {}).score_gen || {};
  const output = (definition || {}).output_extraction || {};
  const schema = output.output_schema;
  // The output contract is chosen in question 4, and this column follows it: a spec on the
  // structured-outputs side must not SEND a format_prompt, so offering an editor for one here would
  // be offering to 422 the next save. Presence, not truthiness — an empty key is a recorded choice.
  const proseDeclared = gen.format_prompt !== undefined && gen.format_prompt !== null;

  const settings = gen.context_settings && typeof gen.context_settings === 'object' ? gen.context_settings : {};
  const [showSettings, setShowSettings] = React.useState(() => Object.keys(settings).length > 0);

  return (
    <div>
      <MicroLabel>The call</MicroLabel>
      <Container top={16}>
        <Row $align="flex-start">
          <ModelSelect value={gen.model} onChange={(v) => setValue('score_gen.model', v)} />
          <Select
            label="Reasoning effort"
            value={gen.reasoning_effort === undefined || gen.reasoning_effort === null ? OMITTED : gen.reasoning_effort}
            onChange={(v) =>
              v === OMITTED ? removeValue('score_gen.reasoning_effort') : setValue('score_gen.reasoning_effort', v)
            }
            options={[{ value: OMITTED, label: 'Omitted — engine default (medium)' }].concat(
              REASONING_EFFORTS.map((e) => ({ value: e, label: e }))
            )}
            hint="Scoring is bounded classification, so low or minimal usually cuts latency without costing accuracy."
          />
        </Row>
      </Container>

      <Container top={16}>
        <Row $align="flex-start">
          <NumberField
            label="Max context chunks"
            min={1}
            value={gen.max_context_chunks}
            onChange={(v) => setValue('score_gen.max_context_chunks', v)}
          />
          <NumberField
            label="Max context tokens"
            min={0}
            value={gen.max_context_tokens}
            onChange={(v) => setValue('score_gen.max_context_tokens', v)}
            hint="0 means the model’s maximum."
          />
          <NumberField
            label="Max output tokens"
            min={1}
            value={gen.max_output_tokens}
            onChange={(v) => setValue('score_gen.max_output_tokens', v)}
            hint="House advice, not a rule: reasoning models spend hidden tokens against this budget, so a low value returns empty completions."
          />
        </Row>
      </Container>

      <Container top={24}>
        <Columns>
          <PromptEditor
            path="prompt"
            label="Prompt"
            tall
            showVariables
            hint="Jinja2, rendered against the event’s selection block."
          />
          {proseDeclared ? (
            <PromptEditor
              path="format_prompt"
              label="Format prompt (legacy)"
              tall
              hint="The prose half of the output contract, deprecated. Still editable on a spec that has one — question 4 converts it to an output_schema."
            />
          ) : (
            <div>
              <MicroLabel>Format prompt</MicroLabel>
              <Container top={8}>
                <Consequence>
                  Not used. This spec declares its shape with an output_schema, and sending a format_prompt as well is a
                  422. Guidance a schema cannot express — a numeric range, a rubric — belongs in the prompt to the left.
                </Consequence>
              </Container>
              <Container top={8}>
                <Body7 isLowContrast>
                  Prose contracts are no longer authorable. Guidance a schema cannot hold belongs in the prompt.
                </Body7>
              </Container>
            </div>
          )}
          <div>
            <MicroLabel>The shape it has to produce</MicroLabel>
            <Container top={8}>
              {schema ? (
                <ReadOnlyJson style={{ maxHeight: 340, overflow: 'auto' }}>
                  {JSON.stringify(schema, null, 2)}
                </ReadOnlyJson>
              ) : (
                <Consequence $tone="warn">
                  No output_schema declared, so the prose contract is the only thing asking for a shape. The model can
                  return something else, and only a real run will show it.
                </Consequence>
              )}
            </Container>
            <Container top={8}>
              <Body7 isLowContrast>Edit it in question 4.</Body7>
            </Container>
          </div>
        </Columns>
      </Container>

      <Container top={24}>
        <TransparentFlatButton
          onClick={() => setShowSettings((current) => !current)}
          buttonProps={{ 'aria-expanded': showSettings, 'aria-controls': CONTEXT_SETTINGS_ID }}
        >
          {showSettings ? 'Hide context toggles' : 'Context toggles'}
        </TransparentFlatButton>
        <Container top={6}>
          <Consequence>
            Every toggle defaults to “no”. A prompt asking about margins gets no fundamentals context unless it is
            switched on here.
          </Consequence>
        </Container>
        {showSettings && (
          <Container top={16}>
            <div
              id={CONTEXT_SETTINGS_ID}
              style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
            >
              {CONTEXT_SETTING_KEYS.map((key) => (
                <Select
                  key={key}
                  label={key}
                  value={settings[key] === undefined ? OMITTED : settings[key]}
                  onChange={(v) =>
                    v === OMITTED
                      ? removeValue(`score_gen.context_settings.${key}`)
                      : setValue(`score_gen.context_settings.${key}`, v)
                  }
                  options={[{ value: OMITTED, label: 'not set — defaults to no' }].concat(
                    CONTEXT_SETTING_VALUES.map((value) => ({ value, label: value }))
                  )}
                />
              ))}
            </div>
          </Container>
        )}
      </Container>

      <Container top={24}>
        <PreviewDock specId={specId} isCreate={isCreate} isDirty={isDirty} />
      </Container>
    </div>
  );
};
